/**
 * Utility to calculate cart taxes with priority:
 * 1. Item-level tax (highest priority)
 * 2. Category-level tax (fallback when item has no tax)
 * 3. No tax (when neither item nor category has tax)
 *
 * Rule: Category tax must NEVER be applied if the item already has its own tax!
 */

/**
 * Extract tax details (array of { id, name, rate }) from an entity's tax config.
 * @param {Object} entity
 * @returns {Array<{ id: string|number, name: string, rate: number }>}
 */
export const extractTaxesFromEntity = (entity) => {
  if (!entity) return [];

  // 1. Check array of taxes in entity.tax_group.taxes, entity.taxes, or entity.tax.taxes FIRST
  const taxesArray = (entity.tax_group && Array.isArray(entity.tax_group.taxes) && entity.tax_group.taxes.length > 0)
    ? entity.tax_group.taxes
    : (Array.isArray(entity.taxes) && entity.taxes.length > 0)
      ? entity.taxes
      : (entity.tax && Array.isArray(entity.tax.taxes) && entity.tax.taxes.length > 0)
        ? entity.tax.taxes
        : null;

  if (taxesArray) {
    const extracted = taxesArray
      .map((t, idx) => ({
        id: t.id || `${entity.id || 't'}-${idx}`,
        name: t.name || t.code || t.tax_name || entity.tax_group?.name || entity.tax?.name || 'Tax',
        rate: Number(t.tax_rate ?? t.rate ?? t.tax_currency_rate ?? t.percentage ?? 0)
      }))
      .filter(t => t.rate > 0);

    if (extracted.length > 0) return extracted;
  }

  // 2. Check single tax object on tax_group or tax property SECOND
  const taxGroup = entity.tax_group || (typeof entity.tax === 'object' ? entity.tax : null);
  if (taxGroup) {
    let rate = Number(taxGroup.tax_rate ?? taxGroup.rate ?? taxGroup.tax_percentage ?? taxGroup.percentage ?? 0);

    // Fallback: Parse percentage rate from tax group name/code if explicit rate field is missing/0 (e.g. "GST 15%")
    if (rate <= 0 && (taxGroup.name || taxGroup.code)) {
      const nameStr = `${taxGroup.name || ''} ${taxGroup.code || ''}`;
      const match = nameStr.match(/(\d+(?:\.\d+)?)\s*%/);
      if (match) {
        rate = parseFloat(match[1]);
      }
    }

    if (rate > 0) {
      return [{
        id: taxGroup.id || taxGroup.name || `${entity.id || 'tg'}-1`,
        name: taxGroup.name || 'Tax',
        rate: rate
      }];
    }
  }

  // 3. Primitive entity.tax as a number or numeric string
  if ((typeof entity.tax === 'number' || typeof entity.tax === 'string') && !isNaN(Number(entity.tax))) {
    const numericTax = Number(entity.tax);
    if (numericTax > 0) {
      return [{
        id: `${entity.id || 'tax'}-1`,
        name: entity.tax_name || 'Tax',
        rate: numericTax
      }];
    }
  }

  // 4. Direct properties (tax_rate, tax_percentage, tax_currency_rate, tax_pricing_type, tax_group_name)
  let directRate = Number(entity.tax_rate ?? entity.tax_percentage ?? entity.tax_currency_rate ?? 0);
  let taxName = entity.tax_name || entity.tax_group_name || 'Tax';

  if (directRate <= 0) {
    const stringToSearch = `${entity.tax_pricing_type || ''} ${entity.tax_group_name || ''} ${entity.tax_name || ''}`;
    const match = stringToSearch.match(/Tax\s*(\d+(?:\.\d+)?)\s*%/i) || stringToSearch.match(/GST\s*(\d+(?:\.\d+)?)\s*%/i) || stringToSearch.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) {
      directRate = parseFloat(match[1]);
      if (!entity.tax_name) {
        taxName = match[0].trim();
      }
    }
  }

  if (directRate > 0) {
    return [{
      id: `${entity.id || 'direct'}-1`,
      name: taxName,
      rate: directRate
    }];
  }

  return [];
};

/**
 * Check if an entity (item or category) has a valid tax configuration.
 * @param {Object} entity
 * @returns {Boolean}
 */
export const hasTaxConfig = (entity) => {
  const taxes = extractTaxesFromEntity(entity);
  return taxes.length > 0;
};

/**
 * Resolve effective taxes for a single item according to priority:
 * 1. Item-level tax (if item defines a valid tax)
 * 2. Category-level tax (fallback if item has no item tax)
 * 3. No tax (if neither has tax)
 *
 * @param {Object} item - Cart item
 * @param {Map|Array|Object} categories - Categories map/array/object
 * @returns {Array<{ id: string|number, name: string, rate: number, source: 'item'|'category' }>}
 */
export const getEffectiveItemTaxes = (item, categories = null) => {
  if (!item) return [];

  // Priority 1: Item-level tax
  const itemTaxes = extractTaxesFromEntity(item);
  if (itemTaxes.length > 0) {
    return itemTaxes.map(t => ({ ...t, source: 'item' }));
  }

  // Priority 2: Category-level tax (only if item has no item-level tax)
  if (categories) {
    const possibleCatIds = [
      item.category,
      item.item_category_id,
      item.category_id,
      item.itemCategory?.id,
      item.item_category?.id
    ].filter(id => id != null && id !== '' && id !== '0' && id !== 0).map(String);

    const possibleCatNames = [
      item.category_name,
      item.categoryName,
      typeof item.category === 'string' && isNaN(Number(item.category)) ? item.category : null,
      typeof item.item_category === 'string' && isNaN(Number(item.item_category)) ? item.item_category : null,
      item.item_category?.name,
      item.itemCategory?.name
    ].filter(Boolean).map(s => String(s).trim().toLowerCase());

    let categoryObj = null;

    if (categories instanceof Map) {
      for (const catId of possibleCatIds) {
        if (categories.has(catId)) {
          categoryObj = categories.get(catId);
          break;
        }
      }
      if (!categoryObj && possibleCatNames.length > 0) {
        for (const cat of categories.values()) {
          if (cat?.name && possibleCatNames.includes(String(cat.name).trim().toLowerCase())) {
            categoryObj = cat;
            break;
          }
        }
      }
    } else if (Array.isArray(categories)) {
      categoryObj = categories.find(c => {
        if (!c) return false;
        const matchesId = possibleCatIds.some(catId => String(c.id) === catId || String(c.item_category_id) === catId);
        if (matchesId) return true;
        const matchesName = possibleCatNames.some(catName => c.name && String(c.name).trim().toLowerCase() === catName);
        return matchesName;
      });
    } else if (typeof categories === 'object') {
      for (const catId of possibleCatIds) {
        if (categories[catId]) {
          categoryObj = categories[catId];
          break;
        }
      }
      if (!categoryObj && possibleCatNames.length > 0) {
        for (const catKey of Object.keys(categories)) {
          const cat = categories[catKey];
          if (cat?.name && possibleCatNames.includes(String(cat.name).trim().toLowerCase())) {
            categoryObj = cat;
            break;
          }
        }
      }
    }

    if (!categoryObj && item.item_category && typeof item.item_category === 'object') {
      categoryObj = item.item_category;
    }
    if (!categoryObj && item.itemCategory && typeof item.itemCategory === 'object') {
      categoryObj = item.itemCategory;
    }

    if (categoryObj) {
      const catTaxes = extractTaxesFromEntity(categoryObj);
      if (catTaxes.length > 0) {
        return catTaxes.map(t => ({ ...t, source: 'category' }));
      }
    }
  }

  // Priority 3: No tax
  return [];
};

/**
 * Calculate complete tax breakdown and total tax amount for a list of cart items.
 *
 * @param {Array} cartItems - List of cart items
 * @param {Map|Array|Object} categories - Available categories data
 * @param {Function} calculateItemPriceFn - Function to compute line item unit price
 * @returns {{ tax_total: number, tax_breakdown: Array<{ tax_name: string, tax_rate: number, tax_amount: number }> }}
 */
export const calculateCartTaxes = (cartItems, categories = null, calculateItemPriceFn = null) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return { tax_total: 0, tax_breakdown: [] };
  }

  const taxMap = new Map(); // key: tax_name + rate -> { tax_name, tax_rate, tax_amount }

  cartItems.forEach(item => {
    const itemUnitPrice = calculateItemPriceFn
      ? calculateItemPriceFn(item)
      : Number(item.flatPrice || item.price || 0);

    const lineTotal = itemUnitPrice * Number(item.quantity || 1);
    if (lineTotal <= 0) return;

    const taxes = getEffectiveItemTaxes(item, categories);

    taxes.forEach(t => {
      const taxAmount = (lineTotal * t.rate) / 100;
      const key = `${t.name}_${t.rate}`;

      if (taxMap.has(key)) {
        const existing = taxMap.get(key);
        existing.tax_amount += taxAmount;
      } else {
        taxMap.set(key, {
          tax_name: t.name,
          tax_rate: t.rate,
          tax_amount: taxAmount
        });
      }
    });
  });

  const tax_breakdown = Array.from(taxMap.values()).map(tb => ({
    tax_name: tb.tax_name,
    tax_rate: tb.tax_rate,
    tax_amount: Number(tb.tax_amount.toFixed(2))
  }));

  const tax_total = Number(
    tax_breakdown.reduce((sum, tb) => sum + tb.tax_amount, 0).toFixed(2)
  );

  return {
    tax_total,
    tax_breakdown
  };
};
